import { Record } from 'immutable';
import {
    isEmpty,
    isUndefined,
    isFinite,
    isBoolean,
    find,
    reduce,
    filter,
    unionBy,
    union,
    differenceBy,
    map,
    flatten,
    includes,
    sumBy,
    forEach,
    concat,
    join,
    orderBy,
    isNull,
    intersection,
} from 'lodash';

import { WindowProxy, ConfigsService } from '@sm360/widget-commons-core-services';

import * as dataTypes from 'commons-vehicle/constants/dataTypes.js';
import * as PaymentFrequencies from 'commons-vehicle/PurchaseMethods/constants/paymentFrequencies.js';

import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';

import FinanceModel from 'commons-vehicle/PurchaseMethods/models/FinanceModel.js';
import CashPurchaseModel from 'commons-vehicle/PurchaseMethods/models/CashPurchaseModel.js';
import DisclaimersModel from 'commons-vehicle/PurchaseMethods/models/DisclaimersModel.js';
/* eslint no-bitwise: 0 */

const MODEL_TRIM_OPTION_CODE = '_MT';

const PurchaseMethodsRecord = Record({
    taxesIncluded: false,
    taxesFactor: 0,
    taxCodes: [],
    tradeIn: 0,
    lien: 0,
    exteriorColourRetail: 0.0,
    exteriorColourResidualRetail: 0.0,
    interiorColourRetail: 0.0,
    interiorColourResidualRetail: 0.0,
    optionsTotalRetail: 0.0,
    optionsTotalResidualRetail: 0.0,
    employeeRebateAdjustment: 0.0,
    highRebate: 0,
    highRebatePurchaseMethod: PURCHASE_METHODS.CASH,
    finance: new FinanceModel(),
    lease: new FinanceModel(),
    cashPurchase: new CashPurchaseModel(),
    availableOptions: [],
    selectedOptions: [],
    configurationOptions: [],
    optionsSelectionResolutions: null,
    disclaimers: new DisclaimersModel(),
});

function checkIsOption(option) {
    return (
        option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
        (option.typeID < dataTypes.DATA_TYPES.FEATURE.TYPES.MODEL_TRIM ||
            option.typeID > dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM)
    );
}

class PurchaseMethods extends PurchaseMethodsRecord {
    constructor(args) {
        const newArgs = {};
        const windowProxy = WindowProxy.get();

        if (windowProxy && windowProxy.localStorage) {
            const { tradeIn, lien } = windowProxy.localStorage;

            if (tradeIn) {
                const parseTradeIn = parseInt(tradeIn, 10);

                // eslint-disable-next-line no-restricted-globals
                newArgs.tradeIn = !isNaN(parseTradeIn) ? parseTradeIn : 0;
            }

            if (lien) {
                const parseLien = parseInt(lien, 10);

                // eslint-disable-next-line no-restricted-globals
                newArgs.lien = !isNaN(parseLien) ? parseLien : 0;
            }
        }

        super({ ...args, ...newArgs });
    }

    getPaymentCalculationParameters() {
        const isLease = this.lease.selected && this.lease.hasValidTerms;
        const selectedFinanceTerm = isLease ? this.lease.getSelectedTerm() : this.finance.getSelectedTerm();

        const {
            taxesIncluded,
            taxesFactor,
            tradeIn,
            lien,
            exteriorColourRetail,
            exteriorColourResidualRetail,
            interiorColourRetail,
            interiorColourResidualRetail,
            optionsTotalRetail,
            optionsTotalResidualRetail,
            dealerFeesTotalRetail,
        } = this;

        return {
            isLease,
            selectedFinanceTerm: this.finance.selectedTerm,
            selectedLeaseTerm: this.lease.selectedTerm,
            paymentFrequency: selectedFinanceTerm ? selectedFinanceTerm.paymentFrequency : PaymentFrequencies.MONTHLY,
            taxesIncluded,
            taxesFactor,
            cashDown: selectedFinanceTerm ? selectedFinanceTerm.cashDown : 0,
            tradeIn,
            lien,
            exteriorColourRetail,
            exteriorColourResidualRetail,
            interiorColourRetail,
            interiorColourResidualRetail,
            optionsTotalRetail,
            optionsTotalResidualRetail,
            dealerFeesTotalRetail,
        };
    }

    getPaymentCalculationWithTerm(term) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        if (paymentCalculationParameters.isLease) {
            paymentCalculationParameters.selectedLeaseTerm = parseInt(term, 10);
        } else {
            paymentCalculationParameters.selectedFinanceTerm = parseInt(term, 10);
        }
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithPaymentFrequency(paymentFrequency) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.paymentFrequency = paymentFrequency;
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithTargetPayment(targetPayment) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.targetPayment = isFinite(targetPayment) ? targetPayment : 0;
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithCashDown(cashDown) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.cashDown = isFinite(cashDown) ? cashDown : 0;
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithTradeIn(tradeIn) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.tradeIn = isFinite(tradeIn) ? tradeIn : 0;
        if (paymentCalculationParameters.tradeIn === 0) {
            paymentCalculationParameters.lien = 0;
        }
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithLien(lien) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        if (paymentCalculationParameters.tradeIn === 0) {
            paymentCalculationParameters.lien = 0;
        } else {
            paymentCalculationParameters.lien = isFinite(lien) ? lien : 0;
        }
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithExteriorColour(exteriorColourAmounts) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.exteriorColourRetail = isFinite(exteriorColourAmounts.retail)
            ? exteriorColourAmounts.retail
            : 0;
        paymentCalculationParameters.exteriorColourResidualRetail =
            paymentCalculationParameters.exteriorColourRetail === 0 || !isFinite(exteriorColourAmounts.residualRetail)
                ? 0
                : exteriorColourAmounts.residualRetail;
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithInteriorColour(interiorColourAmounts) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.interiorColourRetail = isFinite(interiorColourAmounts.retail)
            ? interiorColourAmounts.retail
            : 0;
        paymentCalculationParameters.interiorColourResidualRetail =
            paymentCalculationParameters.interiorColourRetail === 0 || !isFinite(interiorColourAmounts.residualRetail)
                ? 0
                : interiorColourAmounts.residualRetail;
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithOptions(optionsAmounts) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        paymentCalculationParameters.optionsTotalRetail = isFinite(optionsAmounts.retail) ? optionsAmounts.retail : 0;
        paymentCalculationParameters.optionsTotalResidualRetail =
            paymentCalculationParameters.optionsTotalRetail === 0 || !isFinite(optionsAmounts.residualRetail)
                ? 0
                : optionsAmounts.residualRetail;
        return paymentCalculationParameters;
    }

    getPaymentCalculationWithTaxesIncluded(taxesIncluded) {
        const paymentCalculationParameters = this.getPaymentCalculationParameters();
        if (!isBoolean(taxesIncluded)) {
            paymentCalculationParameters.taxesIncluded = false;
        } else {
            paymentCalculationParameters.taxesIncluded = taxesIncluded;
        }
        return paymentCalculationParameters;
    }

    getSelectedFinance() {
        if (this.lease.selected && this.lease.hasValidTerms) {
            return this.lease;
        } else if (this.finance.selected && this.finance.hasValidTerms) {
            return this.finance;
        } else if (this.lease.selected && !this.lease.hasValidTerms && this.finance.hasValidTerms) {
            // for case where lease is selected but not have valid terms, but finance have valid terms
            // fallback to finance
            return this.finance;
        }
        return '';
    }

    getSelectedPurchaseMethodValue() {
        if (this.lease.selected && this.lease.hasValidTerms) {
            return this.lease.purchaseMethodValue;
        } else if (this.finance.selected && this.finance.hasValidTerms) {
            return this.finance.purchaseMethodValue;
        } else if (this.lease.selected && !this.lease.hasValidTerms && this.finance.hasValidTerms) {
            return this.finance.purchaseMethodValue;
        }
        return this.cashPurchase.purchaseMethodValue;
    }

    getSelectedPurchaseMethod() {
        if (this.lease.selected && this.lease.hasValidTerms) {
            return this.lease.purchaseMethod;
        } else if (this.finance.selected && this.finance.hasValidTerms) {
            return this.finance.purchaseMethod;
        } else if (this.lease.selected && !this.lease.hasValidTerms && this.finance.hasValidTerms) {
            return this.finance.purchaseMethod;
        }
        return this.cashPurchase.purchaseMethod;
    }

    getSelectedPurchaseMethodIsValid() {
        const { finance, lease } = this;
        if (
            (finance.selected &&
                (finance.terms.length === 0 || (finance.terms.length === 1 && finance.terms[0].term === 0))) ||
            (lease.selected && (lease.terms.length === 0 || (lease.terms.length === 1 && lease.terms[0].term === 0)))
        ) {
            return false;
        }
        return true;
    }

    // I18n argument is the LocalService class to translate
    getPurchaseMethodLabels(I18n) {
        const purchaseMethodLabels = {
            name: I18n.t('purchaseMethods.cashPurchase'),
            verb: I18n.t('purchaseMethods.cashPurchaseVerb'),
        };

        if (this.lease.selected && this.lease.hasValidTerms) {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.lease');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.leaseVerb');
        } else if (this.finance.selected && this.finance.hasValidTerms) {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.finance');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.financeVerb');
        } else if (this.lease.selected && !this.lease.hasValidTerms && this.finance.hasValidTerms) {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.finance');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.financeVerb');
        }

        return purchaseMethodLabels;
    }

    getSelectedFinanceTerm() {
        let selectedFinanceTerm;
        if (this.lease.selected && this.lease.hasValidTerms) {
            selectedFinanceTerm = this.lease.getSelectedTerm();
        } else if (this.finance.selected) {
            selectedFinanceTerm = this.finance.getSelectedTerm();
        } else if (this.lease.selected && !this.lease.hasValidTerms && this.finance.hasValidTerms) {
            selectedFinanceTerm = this.finance.getSelectedTerm();
        }

        return selectedFinanceTerm;
    }

    getFinancesBestPaymentFrequency() {
        const financeBestPaymentFrequency = this.finance.getBestPaymentFrequency();
        const leaseBestPaymentFrequency = this.lease.getBestPaymentFrequency();
        return financeBestPaymentFrequency < leaseBestPaymentFrequency
            ? financeBestPaymentFrequency
            : leaseBestPaymentFrequency;
    }

    getFinancesAvailablePaymentFrequencies() {
        const financePaymentFrequencies = this.finance.getAvailablePaymentFrequencies();
        const leasePaymentFrequencies = this.lease.getAvailablePaymentFrequencies();

        if (!isEmpty(financePaymentFrequencies) && !isEmpty(leasePaymentFrequencies)) {
            return intersection(financePaymentFrequencies, leasePaymentFrequencies);
        } else if (isEmpty(financePaymentFrequencies) && isEmpty(leasePaymentFrequencies)) {
            return [PaymentFrequencies.MONTHLY];
        }
        return union(financePaymentFrequencies, leasePaymentFrequencies);
    }

    getSelectedFinancePaymentFrequency() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm ? selectedFinanceTerm.paymentFrequency : PaymentFrequencies.MONTHLY;
    }

    getSelectedFinanceTermValue() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm ? selectedFinanceTerm.term : 0;
    }

    // I18n argument is the LocalService class to translate
    getPaymentFrequencyLabels(allFinances, I18n) {
        const paymentFrequencyLabels = {
            periodically: '',
            perPeriod: '',
        };

        const selectedFinanceTerm = this.getSelectedFinanceTerm() || (allFinances && this.finance.getSelectedTerm());
        if (!selectedFinanceTerm) {
            return paymentFrequencyLabels;
        }

        switch (selectedFinanceTerm.paymentFrequency) {
            case PaymentFrequencies.WEEKLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.weekly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perWeek');
                break;
            case PaymentFrequencies.BI_WEEKLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.biWeekly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perBiWeek');
                break;
            case PaymentFrequencies.SEMI_MONTHLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.semiMonthly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perSemiMonth');
                break;
            case PaymentFrequencies.MONTHLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.monthly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perMonth');
                break;
            default:
                break;
        }

        return paymentFrequencyLabels;
    }

    getBestPaymentFinance() {
        const leasePayment = this.lease.getCalculatedPayment();
        const financePayment = this.finance.getCalculatedPayment();

        return leasePayment > 0 && leasePayment < financePayment
            ? this.lease.purchaseMethodValue
            : this.finance.purchaseMethodValue;
    }

    getBestPaymentTerm(maximumTerm) {
        const selectedFinance = this.getSelectedFinance();
        return selectedFinance ? selectedFinance.getBestPaymentTerm(maximumTerm) : undefined;
    }

    getOrderValue(purchaseMethodValue) {
        const cashRetail = this.cashPurchase.getRetail();

        // we're using cashRetail value here if there's no calculatedPayment
        // so it will be sorted at the end of the list as it's a high value
        const leasePayment = this.lease.getCalculatedPayment() || cashRetail;
        const financePayment = this.finance.getCalculatedPayment() || cashRetail;

        switch (purchaseMethodValue) {
            case PURCHASE_METHODS.LEASE:
                return leasePayment;

            case PURCHASE_METHODS.FINANCE:
                return financePayment;

            case PURCHASE_METHODS.FINANCE | PURCHASE_METHODS.LEASE:
                return this.getBestPaymentFinance() === PURCHASE_METHODS.LEASE ? leasePayment : financePayment;

            default:
                return cashRetail;
        }
    }

    getTaxCodesString() {
        return isEmpty(this.taxCodes) ? '' : this.taxCodes.join(' / ');
    }

    getCashDownFromTargetPayment(targetPayment) {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        if (
            !selectedFinanceTerm ||
            isEmpty(selectedFinanceTerm.paymentFrequencies) ||
            isUndefined(selectedFinanceTerm.paymentFrequencies[selectedFinanceTerm.paymentFrequency])
        ) {
            return 0;
        }

        const currentPaymentFrequency = selectedFinanceTerm.paymentFrequencies[selectedFinanceTerm.paymentFrequency];
        const { financeFactor } = currentPaymentFrequency;
        if (financeFactor <= 0) {
            return 0;
        }

        const netPayment = selectedFinanceTerm.get('calculatedNetPayment');
        // eslint-disable-next-line no-restricted-globals
        const targetPaymentValue = isNaN(targetPayment) ? 0 : targetPayment;

        let targetCashdown = (netPayment - targetPaymentValue) / financeFactor;
        if (targetCashdown < 0 || Math.round(targetCashdown) === 0) {
            targetCashdown = 0;
        }
        return targetCashdown;
    }

    // I18n argument is the LocalService class to translate
    getSelectedFinanceKMPlanParameters(I18n) {
        const selectedFinance = this.getSelectedFinance();

        let kmPlanOptions;
        let selectedKMPlan;
        if (selectedFinance) {
            kmPlanOptions = selectedFinance.getKMPlansDropDownValues(I18n);
            selectedKMPlan = find(kmPlanOptions, { value: selectedFinance.annualKMAllowed });
        }

        return selectedFinance
            ? {
                  annualKMAllowed: selectedFinance.annualKMAllowed,
                  endTermKMCost: selectedFinance.endTermKMCost,
                  kmPlanOptions,
                  selectedKMPlan,
              }
            : {
                  annualKMAllowed: 0,
                  endTermKMCost: 0,
                  kmPlanOptions: '',
                  selectedKMPlan: '',
              };
    }

    getSelectedFinanceCalculatedPayments() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        if (!selectedFinanceTerm) {
            return {
                calculatedPaymentBeforeTaxes: 0,
                calculatedPaymentTaxes: 0,
                calculatedPayment: 0,
                minimumPayment: 0,
                maximumPayment: 0,
            };
        }

        const calculatedPayments = selectedFinanceTerm.getCalculatedPayments(this.taxesIncluded);
        return {
            calculatedPaymentBeforeTaxes: calculatedPayments.calculatedPaymentBeforeTaxes,
            calculatedPaymentTaxes: calculatedPayments.calculatedPaymentTaxes,
            calculatedPayment: calculatedPayments.calculatedPayment,
            minimumPayment: 0,
            maximumPayment: selectedFinanceTerm.calculatedNetPayment,
        };
    }

    getSelectedFinanceCashDown() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm ? selectedFinanceTerm.cashDown : 0;
    }

    getSelectedFinanceRebateTaxesAtDelivery() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm ? selectedFinanceTerm.rebatesTaxesAtDelivery : 0;
    }

    getSelectedRebatesAtStandardRate() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm ? selectedFinanceTerm.rebatesAtStandardRate : 0;
    }

    getSelectedPurchaseMethodDiscounts() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return (
            (selectedFinanceTerm ? selectedFinanceTerm.discounts : this.cashPurchase.discounts) +
            this.cashPurchase.optionsAdjustmentDiscounts
        );
    }

    getSelectedPurchaseMethodRebates() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm
            ? selectedFinanceTerm.rebates + selectedFinanceTerm.rebatesTaxes
            : this.cashPurchase.rebates + this.cashPurchase.rebatesTaxes;
    }

    getSelectedPurchaseMethodRebatesTaxes() {
        const isLease = this.lease.selected && this.lease.hasValidTerms;
        if (isLease) {
            return 0;
        }
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        return selectedFinanceTerm ? selectedFinanceTerm.rebatesTaxes : this.cashPurchase.rebatesTaxes;
    }

    getCashPurchaseAmounts() {
        let msrp = this.cashPurchase.getRetail();
        const taxes = msrp * this.taxesFactor;

        let rebates = 0;
        let dealerDiscounts = 0;
        let freightPDI = 0;
        let taxAndDuty = 0;
        let dealerIncentives = 0;
        const allSelectedOptions = this.getAllSelectedOptions();
        if (!isEmpty(allSelectedOptions)) {
            rebates = this.getRebatesAmount(allSelectedOptions) + this.getDiscountsAmount(allSelectedOptions);
            dealerDiscounts = this.getDealerDiscountsAmount(allSelectedOptions);
            dealerIncentives = this.getDealerIncentivesAmount(allSelectedOptions);

            if (!rebates) {
                rebates = this.cashPurchase.getTotalRebates();
            }

            freightPDI = this.getFreightAndPDIFeesAmount(allSelectedOptions, false);
            taxAndDuty =
                this.getTaxesAndDutiesAmount(allSelectedOptions, false) +
                this.getOtherFeesAmount(allSelectedOptions, false) +
                this.cashPurchase.dealerFees;
        } else {
            rebates = this.cashPurchase.getTotalRebates();
        }
        if (this.cashPurchase.optionsAdjustmentDiscounts === 0) {
            rebates += this.employeeRebateAdjustment;
        }

        msrp -= freightPDI;
        msrp -= taxAndDuty;
        msrp -= rebates;

        return {
            msrp,
            rebates,
            dealerDiscounts,
            freightPDI,
            taxAndDuty,
            taxes,
            dealerIncentives,
        };
    }

    // The includeCashPurchaseOnlyDiscount flag is used to enforce the inclusion of the cash only dealer discount
    getPurchaseMethodSelectedOptions(includeCashPurchaseOnlyDiscount) {
        return this.cashPurchase.selected
            ? this.selectedOptions
            : filter(
                  this.selectedOptions,
                  (selectedOption) =>
                      includeCashPurchaseOnlyDiscount || !selectedOption.isDealerCashPurchaseOnlyDiscount()
              );
    }

    // The includeCashPurchaseOnlyDiscount flag is used to enforce the inclusion of the cash only dealer discount
    getAllSelectedOptions(includeCashPurchaseOnlyDiscount) {
        return unionBy(
            this.configurationOptions,
            this.getPurchaseMethodSelectedOptions(includeCashPurchaseOnlyDiscount),
            'selectionCode'
        );
    }

    getNonConfigurationSelectedOptions() {
        return differenceBy(this.getPurchaseMethodSelectedOptions(), this.configurationOptions, 'selectionCode');
    }

    getSelectedPurchaseMethodAmounts() {
        const { tradeIn, lien } = this;
        const cashDown = this.getSelectedFinanceCashDown();

        let modelTrim =
            this.cashPurchase.retail -
            this.cashPurchase.pricingAmounts.freightPDI -
            this.cashPurchase.pricingAmounts.taxesAndDuties;
        let exteriorColour = 0;
        let interiorColour = 0;
        let options = this.cashPurchase.optionsAdjustment;
        let discounts = this.getSelectedPurchaseMethodDiscounts();

        if (!this.cashPurchase.selected || this.cashPurchase.optionsAdjustmentDiscounts === 0) {
            discounts += this.employeeRebateAdjustment;
        }

        let rebates = this.getSelectedPurchaseMethodRebates();
        let { freightPDI } = this.cashPurchase.pricingAmounts;
        let otherFees = 0;
        let taxAndDuty = this.cashPurchase.pricingAmounts.taxesAndDuties;
        let protections = 0;
        const { deliveryFeeAmount, deliveryTaxes, deposit } = this.getSelectedPurchaseMethodDeliveryAmounts();

        let taxes =
            this.getSelectedPurchaseMethodValue() === PURCHASE_METHODS.LEASE
                ? 0
                : this.getRoundedAmount(
                      this.taxesFactor *
                          (modelTrim +
                              exteriorColour +
                              interiorColour +
                              options +
                              freightPDI +
                              otherFees +
                              taxAndDuty +
                              protections -
                              (discounts + this.tradeIn))
                  );

        if (this.cashPurchase.retailAdjustment) {
            modelTrim -= this.cashPurchase.retailAdjustment;
        }

        let allInPrice =
            modelTrim +
            exteriorColour +
            interiorColour +
            options +
            freightPDI +
            otherFees +
            taxAndDuty +
            protections -
            discounts;

        let totalPriceBeforeTaxes =
            modelTrim +
            exteriorColour +
            interiorColour +
            options +
            freightPDI +
            otherFees +
            taxAndDuty +
            protections -
            (discounts + this.tradeIn);

        let totalPriceAfterTaxes = totalPriceBeforeTaxes + taxes + lien - (rebates + cashDown);

        let totalDueAtDelivery = totalPriceAfterTaxes + deliveryFeeAmount + deliveryTaxes + deposit;

        const allSelectedOptions = this.getAllSelectedOptions();
        if (isEmpty(allSelectedOptions) || this.hasOnlyModelTrimOption(allSelectedOptions)) {
            return {
                modelTrim,
                exteriorColour,
                interiorColour,
                options,
                discounts,
                freightPDI,
                otherFees,
                taxAndDuty,
                rebates,
                protections,
                taxes: taxes < 0 ? 0 : taxes,
                tradeIn,
                lien,
                cashDown,
                totalPriceBeforeTaxes,
                allInPrice,
                totalPriceAfterTaxes,
                totalDueAtDelivery,
            };
        }
        const configurationOptionsDiscounts = isEmpty(this.configurationOptions) ? 0 : discounts;

        modelTrim = this.getModelTrimAmount(allSelectedOptions);
        exteriorColour = this.getExteriorColourAmount(allSelectedOptions);
        interiorColour = this.getInteriorColourAmount(allSelectedOptions);
        options = this.getOptionsAmount(allSelectedOptions) + this.getOptionsAmountIncludedInPrice(allSelectedOptions);
        const discountAmount = this.getDiscountsAmount(allSelectedOptions);
        const dealerDiscountAmount = this.getDealerDiscountsAmount(allSelectedOptions);
        const dealerIncentivesAmount = this.getDealerIncentivesAmount(allSelectedOptions);
        const discountsFromOptions = discountAmount + dealerDiscountAmount - dealerIncentivesAmount;

        discounts = discountsFromOptions > 0 ? discountsFromOptions + configurationOptionsDiscounts : discounts;
        const rebatesFromOptions = this.getRebatesAmount(allSelectedOptions);
        rebates = rebatesFromOptions > 0 ? rebatesFromOptions : rebates;
        freightPDI = this.getFreightAndPDIFeesAmount(allSelectedOptions, false);
        otherFees = this.getOtherFeesAmount(allSelectedOptions, false);
        taxAndDuty = this.getTaxesAndDutiesAmount(allSelectedOptions, false);
        protections = this.getProtectionsAmount(allSelectedOptions);

        taxes =
            this.getSelectedPurchaseMethodValue() === PURCHASE_METHODS.LEASE
                ? 0
                : this.getRoundedAmount(
                      this.taxesFactor *
                          (modelTrim +
                              exteriorColour +
                              interiorColour +
                              options +
                              freightPDI +
                              otherFees +
                              taxAndDuty +
                              protections -
                              (discounts + this.tradeIn))
                  );

        allInPrice =
            modelTrim +
            exteriorColour +
            interiorColour +
            options +
            freightPDI +
            otherFees +
            taxAndDuty +
            protections -
            discounts;

        totalPriceBeforeTaxes =
            modelTrim +
            exteriorColour +
            interiorColour +
            options +
            freightPDI +
            otherFees +
            taxAndDuty +
            protections -
            (discounts + this.tradeIn);

        totalPriceAfterTaxes = totalPriceBeforeTaxes + taxes + lien - (rebates + cashDown);

        totalDueAtDelivery = totalPriceAfterTaxes + deliveryFeeAmount + deliveryTaxes + deposit;

        return {
            modelTrim,
            exteriorColour,
            interiorColour,
            options,
            discounts,
            freightPDI,
            otherFees,
            taxAndDuty,
            rebates,
            protections,
            taxes: taxes < 0 ? 0 : taxes,
            tradeIn,
            lien,
            cashDown,
            totalPriceBeforeTaxes,
            allInPrice,
            totalPriceAfterTaxes,
            totalDueAtDelivery,
        };
    }

    getSelectedPurchaseMethodResidualAmounts() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();

        return selectedFinanceTerm
            ? selectedFinanceTerm.residualAmount +
                  selectedFinanceTerm.optionsResidualRetail * (selectedFinanceTerm.residualRate / 100)
            : 0;
    }

    getSelectedPurchaseMethodDeliveryAmounts() {
        const allSelectedOptions = this.getAllSelectedOptions();
        const deliveryFee = this.getDeliveryFee();
        const deliveryFeeAmount = deliveryFee ? deliveryFee.retail : 0;
        const deliveryCashDown = this.getSelectedFinanceCashDown();
        const deliveryRebatesTaxes = this.getSelectedFinanceRebateTaxesAtDelivery();
        const isLease = this.lease.selected && this.lease.hasValidTerms;
        const deliveryTaxes = isLease
            ? this.getRoundedAmount(this.taxesFactor * (deliveryFeeAmount + deliveryCashDown))
            : this.getRoundedAmount(this.taxesFactor * deliveryFeeAmount);

        const deposit = this.getDepositAmount(allSelectedOptions);
        const { calculatedPayment } = this.getSelectedFinanceCalculatedPayments();
        const upfrontPayment = isLease ? calculatedPayment : 0;

        return {
            deliveryFeeAmount,
            deliveryCashDown,
            deliveryTaxes,
            deliveryRebatesTaxes,
            deposit,
            upfrontPayment,
        };
    }

    getRoundedAmount(amount) {
        return Math.round(amount * 100) / 100;
    }

    getModelTrimAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        option.typeID >= dataTypes.DATA_TYPES.FEATURE.TYPES.MODEL_TRIM &&
                        option.typeID <= dataTypes.DATA_TYPES.FEATURE.TYPES.TRANSMISSION
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getDiscountsAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.REBATE.DATA_TYPE_ID &&
                        option.typeID !== dataTypes.DATA_TYPES.REBATE.TYPES.DAA_CASH_DOWN &&
                        option.isTaxable &&
                        !option.isDealerDiscount()
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getExteriorColourAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.EXTERIOR_COLOUR
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getExteriorColourResidualizingAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.EXTERIOR_COLOUR
                            ? option.residualizingAmount
                            : 0)
                    );
                },
                0
            )
        );
    }

    getInteriorColourAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getInteriorColourResidualizingAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM
                            ? option.residualizingAmount
                            : 0)
                    );
                },
                0
            )
        );
    }

    getOptionsAmount(options) {
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();

        let res;

        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    if (isShowroomV25Enabled) {
                        res =
                            sum +
                            (checkIsOption(option) && option.isIncludedInPriceOrPayment === false ? option.retail : 0);
                    } else {
                        res =
                            sum +
                            (checkIsOption(option) && (option.supplier === 'D' || option.supplier === 'O')
                                ? option.retail
                                : 0);
                    }
                    return res;
                },
                0
            )
        );
    }

    getOptionsAmountIncludedInPrice(options) {
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();

        let res;

        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    if (isShowroomV25Enabled) {
                        res =
                            sum +
                            (checkIsOption(option) && option.isIncludedInPriceOrPayment === true ? option.retail : 0);
                    } else {
                        res = 0;
                    }
                    return res;
                },
                0
            )
        );
    }

    getOptionsResidualizingAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        (option.typeID < dataTypes.DATA_TYPES.FEATURE.TYPES.MODEL_TRIM ||
                            option.typeID > dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM)
                            ? option.residualizingAmount
                            : 0)
                    );
                },
                0
            )
        );
    }

    getRebatesAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.REBATE.DATA_TYPE_ID &&
                        option.typeID !== dataTypes.DATA_TYPES.REBATE.TYPES.DAA_CASH_DOWN &&
                        !option.isTaxable &&
                        !option.isDealerDiscount()
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getFreightAndPDIFeesAmount(options, dueAtDelivery) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (((dueAtDelivery && option.dueAtDelivery) || (!dueAtDelivery && !option.dueAtDelivery)) &&
                        (option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                            (option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.FREIGHT ||
                                option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.PREPARATION))
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getOtherFeesAmount(options, dueAtDelivery) {
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled();
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    if (isShowroomV25Enabled) {
                        return (
                            sum +
                            (((dueAtDelivery && option.dueAtDelivery) || (!dueAtDelivery && !option.dueAtDelivery)) &&
                            (option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                                option.typeID > dataTypes.DATA_TYPES.FEE.TYPES.PREPARATION &&
                                option.typeID !== dataTypes.DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT &&
                                option.typeID !== dataTypes.DATA_TYPES.FEE.TYPES.RESERVATION_DEPOSIT)
                                ? option.retail
                                : 0)
                        );
                    }
                    return (
                        sum +
                        (((dueAtDelivery && option.dueAtDelivery) || (!dueAtDelivery && !option.dueAtDelivery)) &&
                        ((option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                            option.typeID > dataTypes.DATA_TYPES.FEE.TYPES.PREPARATION &&
                            option.typeID !== dataTypes.DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT &&
                            option.typeID !== dataTypes.DATA_TYPES.FEE.TYPES.RESERVATION_DEPOSIT) ||
                            (checkIsOption(option) && (option.supplier !== 'D' && option.supplier !== 'O')))
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getOtherFeesAmountIncluded(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (!option.dueAtDelivery &&
                        option.isIncludedInPriceOrPayment &&
                        option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                        option.typeID > dataTypes.DATA_TYPES.FEE.TYPES.PREPARATION &&
                        option.typeID !== dataTypes.DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT &&
                        option.typeID !== dataTypes.DATA_TYPES.FEE.TYPES.RESERVATION_DEPOSIT
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getProtectionsAmountIncluded(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (!option.dueAtDelivery &&
                        option.isIncludedInPriceOrPayment &&
                        option.dataType === dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getAccessoriesAmountIncluded(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (!option.dueAtDelivery &&
                        option.isIncludedInPriceOrPayment &&
                        option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getTaxesAndDutiesAmount(options, dueAtDelivery) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (((dueAtDelivery && option.dueAtDelivery) || (!dueAtDelivery && !option.dueAtDelivery)) &&
                        option.dataType === dataTypes.DATA_TYPES.TAX_AND_DUTY.DATA_TYPE_ID
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getProtectionsAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return sum + (option.dataType === dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID ? option.retail : 0);
                },
                0
            )
        );
    }

    getDealerDiscountsAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return sum + (option.isDealerDiscount() ? option.retail : 0);
                },
                0
            )
        );
    }

    getSelectedPurchaseMethodRetailOrPayment() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        if (!selectedFinanceTerm) {
            return this.cashPurchase.getRetail() - this.cashPurchase.getTotalRebates();
        }
        return selectedFinanceTerm.calculatedPayment;
    }

    getTradeInWithTaxCredits() {
        return this.tradeIn + this.cashPurchase.tradeInTaxesAdjustment;
    }

    getDepositAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                        (option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT ||
                            option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.RESERVATION_DEPOSIT)
                            ? option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }

    getDeliveryFee() {
        let fee = find(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.DELIVERY
            );
        });

        if (!fee) {
            fee = find(this.availableOptions, (option) => {
                return (
                    option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                    option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.DELIVERY
                );
            });
        }
        return fee;
    }

    getCheckoutDeposit() {
        let deposit = find(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT
            );
        });

        if (!deposit) {
            deposit = find(this.availableOptions, (option) => {
                return (
                    option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                    option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.CHECKOUT_DEPOSIT
                );
            });
        }
        return deposit;
    }

    getReservationDeposit() {
        let deposit = find(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.RESERVATION_DEPOSIT
            );
        });

        if (!deposit) {
            deposit = find(this.availableOptions, (option) => {
                return (
                    option.dataType === dataTypes.DATA_TYPES.FEE.DATA_TYPE_ID &&
                    option.typeID === dataTypes.DATA_TYPES.FEE.TYPES.RESERVATION_DEPOSIT
                );
            });
        }
        return deposit;
    }

    getSelectedExteriorColourCode() {
        const selectedOption = find(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.EXTERIOR_COLOUR
            );
        });
        return selectedOption ? selectedOption.code : '';
    }

    getSelectedInteriorColourCode() {
        const selectedOption = find(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM
            );
        });
        return selectedOption ? selectedOption.code : '';
    }

    getSelectedOptionsPackage() {
        return filter(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.PACKAGE
            );
        });
    }

    getSelectedOptions() {
        return filter(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                (option.typeID < dataTypes.DATA_TYPES.FEATURE.TYPES.MODEL_TRIM ||
                    option.typeID > dataTypes.DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM)
            );
        });
    }

    getSelectedProtections() {
        return filter(this.getAllSelectedOptions(), (option) => {
            return option.dataType === dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID;
        });
    }

    getSelectedProtectionsPackages() {
        const selectedProtections = this.getSelectedProtections();
        const selectedProtectionsPackages = filter(
            selectedProtections,
            (protection) => !isEmpty(protection.includedCodes)
        );
        if (isEmpty(selectedProtectionsPackages)) {
            return selectedProtections;
        }

        const includedCodes = flatten(
            map(selectedProtectionsPackages, (protection) => [...protection.includedCodes, protection.selectionCode])
        );

        const selectedSingleProtections = filter(
            selectedProtections,
            (protection) => !includes(includedCodes, protection.selectionCode)
        );
        const packagedProtections = filter(selectedProtections, (protection) =>
            includes(includedCodes, protection.selectionCode)
        );

        const selectedPackagedProtections = [];
        forEach(selectedProtectionsPackages, (protectionsPackage) => {
            const packageIncludedProtections = filter(packagedProtections, (protection) =>
                includes(protectionsPackage.includedCodes, protection.selectionCode)
            );

            const packageDescription = isEmpty(packageIncludedProtections)
                ? protectionsPackage.description
                : join(map(packageIncludedProtections, (protection) => protection.label), ', ');

            const packageIncludedProtectionsRetail = sumBy(
                packageIncludedProtections,
                (protection) => protection.retail
            );
            const packageIncludedProtectionsResidual = sumBy(
                packageIncludedProtections,
                (protection) => protection.residualizingAmount
            );

            selectedPackagedProtections.push(
                protectionsPackage
                    .set('description', packageDescription)
                    .set('retail', protectionsPackage.retail + packageIncludedProtectionsRetail)
                    .set(
                        'residualizingAmount',
                        protectionsPackage.residualizingAmount + packageIncludedProtectionsResidual
                    )
            );
        });

        return orderBy(concat(selectedPackagedProtections, selectedSingleProtections), 'keyOrder');
    }

    getSelectedOptionsAndColours() {
        return filter(this.getAllSelectedOptions(), (option) => {
            return (
                option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                (option.typeID < dataTypes.DATA_TYPES.FEATURE.TYPES.MODEL_TRIM ||
                    option.typeID > dataTypes.DATA_TYPES.FEATURE.TYPES.TRANSMISSION)
            );
        });
    }

    getSelectedDealerFees() {
        return filter(this.getAllSelectedOptions(), (option) => option.isDealerFee());
    }

    // The includeCashPurchaseOnlyDiscount flag is used to enforce the inclusion of the cash only dealer discount
    getSelectedDealerDiscounts(includeCashPurchaseOnlyDiscount) {
        return filter(this.getAllSelectedOptions(includeCashPurchaseOnlyDiscount), (option) => {
            return option.isDealerDiscount();
        });
    }

    getElectricRebate() {
        const selectedTerm = this.getSelectedFinanceTerm();
        return selectedTerm ? selectedTerm.getElectricRebate() : this.cashPurchase.getElectricRebate();
    }

    getBonusRebate() {
        const bonusRebate = {
            amount: 0,
            cashPurchase: false,
            lease: false,
            finance: false,
        };

        const leaseSelectedTerm = this.lease.getSelectedTerm();
        if (leaseSelectedTerm && leaseSelectedTerm.rebatesAtDelivery > 0) {
            bonusRebate.amount = leaseSelectedTerm.rebatesAtDelivery;
            bonusRebate.lease = true;
        }

        const financeSelectedTerm = this.finance.getSelectedTerm();
        if (financeSelectedTerm && financeSelectedTerm.rebatesAtDelivery > 0) {
            bonusRebate.amount =
                bonusRebate.amount > financeSelectedTerm.rebatesAtDelivery
                    ? financeSelectedTerm.rebatesAtDelivery
                    : bonusRebate.amount;
            bonusRebate.finance = true;
        }

        if (this.cashPurchase.rebatesAtDelivery > 0) {
            bonusRebate.amount =
                bonusRebate.amount > this.cashPurchase.rebatesAtDelivery
                    ? this.cashPurchase.rebatesAtDelivery
                    : bonusRebate.amount;
            bonusRebate.cashPurchase = true;
        }

        return bonusRebate;
    }

    getEmployeeRebate() {
        const selectedTerm = this.getSelectedFinanceTerm();
        return selectedTerm ? selectedTerm.getEmployeeRebate() : this.cashPurchase.getEmployeeRebate();
    }

    getDisclaimerParameters() {
        const { cashPurchase, finance, lease } = this;

        const cashPurchaseDisclaimer = {
            purchaseMethod: cashPurchase.purchaseMethodValue,
            terms: [0],
            frequencies: [0],
        };
        const financeDisclaimer = finance.getDisclaimerParameters();
        const leaseDisclaimer = lease.getDisclaimerParameters();

        const disclaimerParameters = [cashPurchaseDisclaimer];
        if (!isNull(financeDisclaimer)) {
            disclaimerParameters.push(financeDisclaimer);
        }
        if (!isNull(leaseDisclaimer)) {
            disclaimerParameters.push(leaseDisclaimer);
        }

        return disclaimerParameters;
    }

    getDisclaimersForCurrentMethod() {
        const { disclaimers } = this;
        switch (this.getSelectedPurchaseMethodValue()) {
            case PURCHASE_METHODS.FINANCE:
                return disclaimers.finance;
            case PURCHASE_METHODS.LEASE:
                return disclaimers.lease;
            case PURCHASE_METHODS.CASH:
                return disclaimers.cashPurchase;
            default:
                return null;
        }
    }

    hasOnlyModelTrimOption(options) {
        return options && options.length === 1 && options[0].code === MODEL_TRIM_OPTION_CODE;
    }

    getSelectedFinanceCostOfCredit() {
        const term = this.getSelectedFinanceTerm();
        const cashOnlyRebates = this.getSelectedFinanceCashOnlyRebates();
        const { costOfCredit } =
            term && term.getDisclaimerAmounts
                ? term.getDisclaimerAmounts(!this.lease.selected, 0, 0)
                : { costOCredit: 0 };

        return this.lease.selected ? costOfCredit : costOfCredit + cashOnlyRebates;
    }

    getSelectedFinanceSellingPrice() {
        const term = this.getSelectedFinanceTerm();
        const financeSellingPrice = term && term.getFinancedSellingPrice ? term.getFinancedSellingPrice() : 0;

        return financeSellingPrice;
    }

    getSelectedFinanceCashOnlyRebates() {
        const cashAmountRebates = this.cashPurchase.getTotalRebatesWithTaxes();
        const discount = this.getSelectedPurchaseMethodDiscounts();
        const rebates = this.getSelectedPurchaseMethodRebates();

        const cashOnlyRebates = cashAmountRebates - discount - rebates;

        return cashOnlyRebates > 0 ? cashOnlyRebates : 0;
    }

    getDealerIncentivesAmount(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return sum + (option.isDealerIncentive() ? option.retail : 0);
                },
                0
            )
        );
    }
    getOptionsAdjustmentFCA(options) {
        return this.getRoundedAmount(
            reduce(
                options,
                (sum, option) => {
                    return (
                        sum +
                        (option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                        option.typeID === dataTypes.DATA_TYPES.FEATURE.TYPES.FCA
                            ? -1 * option.retail
                            : 0)
                    );
                },
                0
            )
        );
    }
}

export default PurchaseMethods;
